/**
 * Twin Storage Utility - Persistent multi-twin management using localStorage
 * Single source of truth for all twin data across the application
 */

export interface Machine {
  name: string;
  type: string;
  sensors: string[];
}

export interface Twin {
  id: string;
  name: string;
  company: string;
  factoryType: string;
  machines: Machine[];
  sensors: string[];
  workspaceId: string;
  twinMakerWsId: string;
  factoryId: string;
  machineCount: number;
  sensorCount: number;
  createdAt: string;
  updatedAt: string;
  // AWS Connection fields (optional)
  isAWSConnected?: boolean;
  awsRegion?: string;
  awsAccessKeyId?: string; // Should be encrypted in production
  awsSecretAccessKey?: string; // Should be encrypted in production
  awsLastSync?: string;
}

export interface TwinsStore {
  twins: Twin[];
  activeTwinId: string | null;
}

const STORAGE_KEY = 'twinverse_twins';
const ACTIVE_TWIN_KEY = 'twinverse_activeTwinId';

/**
 * Get all stored twins
 */
export function getAllTwins(): Twin[] {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return [];
  try {
    return JSON.parse(stored);
  } catch (e) {
    console.error('Failed to parse twins:', e);
    return [];
  }
}

/**
 * Get the currently active twin
 */
export function getActiveTwin(): Twin | null {
  const activeId = localStorage.getItem(ACTIVE_TWIN_KEY);
  if (!activeId) return null;

  const twins = getAllTwins();
  return twins.find(t => t.id === activeId) || null;
}

/**
 * Get a specific twin by ID
 */
export function getTwinById(id: string): Twin | null {
  const twins = getAllTwins();
  return twins.find(t => t.id === id) || null;
}

/**
 * Save a new twin and set it as active
 */
export function saveTwin(twinData: Omit<Twin, 'id' | 'createdAt' | 'updatedAt'>): Twin {
  const twins = getAllTwins();
  
  const newTwin: Twin = {
    ...twinData,
    id: `twin_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  twins.push(newTwin);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(twins));
  setActiveTwin(newTwin.id);

  return newTwin;
}

/**
 * Update an existing twin
 */
export function updateTwin(id: string, updates: Partial<Twin>): Twin | null {
  const twins = getAllTwins();
  const index = twins.findIndex(t => t.id === id);
  
  if (index === -1) return null;

  const updated = {
    ...twins[index],
    ...updates,
    id: twins[index].id, // Prevent ID changes
    createdAt: twins[index].createdAt, // Prevent creation date changes
    updatedAt: new Date().toISOString(),
  };

  twins[index] = updated;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(twins));
  return updated;
}

/**
 * Set the active twin
 */
export function setActiveTwin(twinId: string): boolean {
  const twins = getAllTwins();
  if (!twins.find(t => t.id === twinId)) {
    console.error(`Twin ${twinId} not found`);
    return false;
  }

  localStorage.setItem(ACTIVE_TWIN_KEY, twinId);
  return true;
}

/**
 * Delete a twin
 */
export function deleteTwin(id: string): boolean {
  const twins = getAllTwins();
  const filtered = twins.filter(t => t.id !== id);

  if (filtered.length === twins.length) {
    console.error(`Twin ${id} not found`);
    return false;
  }

  localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));

  // Clear active twin if it was deleted
  if (localStorage.getItem(ACTIVE_TWIN_KEY) === id) {
    const nextTwin = filtered[0];
    if (nextTwin) {
      setActiveTwin(nextTwin.id);
    } else {
      localStorage.removeItem(ACTIVE_TWIN_KEY);
    }
  }

  return true;
}

/**
 * Clear all twins and active selection
 */
export function clearAllTwins(): void {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(ACTIVE_TWIN_KEY);
}

/**
 * Get demo twin for initial app state
 */
export function getDemoTwin(): Twin {
  return {
    id: 'demo_cookiefactory',
    name: 'CookieFactory Demo',
    company: 'CookieFactory Inc.',
    factoryType: 'fmcg',
    machines: [
      { name: 'Mixer 1', type: 'Mixer', sensors: ['Temperature', 'RPM', 'Vibration'] },
      { name: 'Mixer 2', type: 'Mixer', sensors: ['Temperature', 'RPM'] },
      { name: 'Freezer 1', type: 'Freezer', sensors: ['Temperature', 'Humidity'] },
    ],
    sensors: ['Temperature', 'RPM', 'Vibration', 'Humidity'],
    workspaceId: 'demo_default',
    twinMakerWsId: 'ws_demo_cookiefactory',
    factoryId: 'factory_demo_001',
    machineCount: 3,
    sensorCount: 4,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Check if a twin is the demo twin
 */
export function isDemo(twin: Twin | null): boolean {
  return twin?.id === 'demo_cookiefactory';
}

/**
 * Initialize with demo twin if no twins exist
 */
export function initializeDemo(): void {
  const twins = getAllTwins();
  if (twins.length === 0) {
    const demo = getDemoTwin();
    localStorage.setItem(STORAGE_KEY, JSON.stringify([demo]));
    localStorage.setItem(ACTIVE_TWIN_KEY, demo.id);
  }
}

/**
 * Check if a twin is AWS-connected
 */
export function isAWSConnected(twin: Twin | null): boolean {
  return twin?.isAWSConnected === true || twin?.factoryType === 'aws-connected';
}

/**
 * Get AWS region for connected twin
 */
export function getAWSRegion(twin: Twin | null): string {
  return twin?.awsRegion || 'us-east-1';
}

/**
 * Update AWS sync timestamp for a twin
 */
export function updateAWSSync(id: string): boolean {
  return updateTwin(id, { awsLastSync: new Date().toISOString() }) !== null;
}

/**
 * Get all AWS-connected twins
 */
export function getAWSConnectedTwins(): Twin[] {
  const twins = getAllTwins();
  return twins.filter(t => isAWSConnected(t));
}

/**
 * Get all created (non-demo) twins
 */
export function getCreatedTwins(): Twin[] {
  const twins = getAllTwins();
  return twins.filter(t => !isDemo(t));
}
